import React from 'react';
import type { TemplateField } from '../../types/admin/admin.types';
import { MultiSelect, SearchSelect } from './ui/Selects';

interface Props {
  field: TemplateField;
  value: unknown;
  onChange: (value: unknown) => void;
  referenceOptions?: Array<{ id: number; label: string }>;
  disabled?: boolean;
}

const inputClass =
  'w-full rounded-md border border-[var(--color-gray-300)] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--color-primary-light)]';

/** Renders one form control based on a template field's type + rules. */
const DynamicFieldInput: React.FC<Props> = ({ field, value, onChange, referenceOptions = [], disabled }) => {
  const rules = field.validation_rules || {};
  const label = (
    <label className="block text-sm font-medium text-[var(--color-gray-700)] mb-1">
      {field.label}
      {rules.required ? <span className="text-[var(--color-error)]"> *</span> : null}
    </label>
  );

  switch (field.field_type) {
    case 'number':
      return (
        <div>
          {label}
          <input
            type="number"
            className={inputClass}
            value={value == null ? '' : (value as number)}
            min={rules.min as number | undefined}
            max={rules.max as number | undefined}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          />
        </div>
      );
    case 'date':
      return (
        <div>
          {label}
          <input
            type="date"
            className={inputClass}
            value={(value as string) || ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    case 'boolean':
      return (
        <label className="flex items-center gap-2 text-sm text-[var(--color-gray-700)]">
          <input
            type="checkbox"
            checked={!!value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
          />
          {field.label}
        </label>
      );
    case 'dropdown':
      return (
        <div>
          {label}
          <select
            className={inputClass}
            value={(value as string) || ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">— select —</option>
            {(rules.options || []).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      );
    case 'reference': {
      const multiple = !!rules.multiple;
      if (multiple) {
        const vals = Array.isArray(value) ? (value as number[]) : value != null ? [Number(value)] : [];
        return (
          <div>
            {label}
            <MultiSelect options={referenceOptions} value={vals} onChange={(ids) => onChange(ids)} disabled={disabled} placeholder="Select one or more…" />
          </div>
        );
      }
      const single = value != null ? Number(Array.isArray(value) ? value[0] : value) : null;
      return (
        <div>
          {label}
          <SearchSelect options={referenceOptions} value={Number.isFinite(single as number) ? single : null} onChange={(id) => onChange(id)} disabled={disabled} placeholder="Select…" />
        </div>
      );
    }
    case 'text':
    default:
      return (
        <div>
          {label}
          <input
            type="text"
            className={inputClass}
            value={(value as string) || ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
  }
};

export default DynamicFieldInput;
